'use client';

import { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide';
import type { Options } from '@splidejs/splide';
import '@splidejs/react-splide/css';
import styles from './videogroup.module.scss';
import { ChevronIcon } from '@/assets/icons/ChevronIcon';
import { responsiveBreakpointsPx } from '@/constants';
import { Video, VideoProps } from '../Video/Video';
import { Modal } from '../../Modal/Modal';
import { VideoPlayer } from '../Video/VideoPlayer';
import useViewportWidth from '@/hooks/useViewportWidth';
import useClickOutside from '@/hooks/useClickOutside';
// TODO: dynamically import this component once migrated into de-sxm-www
import { FocusTrap } from 'focus-trap-react';
import { Button } from '@/ui-components/atoms/Button/Button';

export interface VideoGroupProps {
    videoSlides: VideoProps[];
    sliderAriaLabel?: string;
    className?: string;
    inlineVideo?: React.ReactElement<typeof Video>;
}

export function VideoGroup({
    videoSlides,
    sliderAriaLabel,
    className,
    inlineVideo,
}: VideoGroupProps): React.JSX.Element {
    const MODAL_CLICK_THROUGH_EXCEPTIONS_SELECTOR = '#onetrust-consent-sdk';
    const SLIDE_TRANSITION_SPEED_MS = 300;

    const splideRef = useRef<Splide | null>(null);
    const ref = useRef<HTMLDivElement>(null);
    const generalSplideRef = useRef<Splide | null>(null);
    const [focusPause, setFocusPause] = useState(false);
    const [generalActiveIndex, setGeneralActiveIndex] = useState(-1);
    const [activeIndex, setActiveIndex] = useState(-1);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [showArrows, setShowArrows] = useState(true);
    const [splideKey, setSplideKey] = useState(0);
    const [modalSlideSpeed, setModalSlideSpeed] = useState(SLIDE_TRANSITION_SPEED_MS);
    const syncTimesRef = useRef<Record<number, number>>({});
    const currentVideoTimesRef = useRef<Record<number, number>>({});
    const sliderClassNames = cn(styles.slider, className);

    const prevArrowClassNames = cn('splide__arrow splide__arrow--prev', styles.prevArrow);
    const nextArrowClassNames = cn('splide__arrow splide__arrow--next', styles.nextArrow);

    const modalPrevArrowClassNames = cn('splide__arrow splide__arrow--prev', styles.modalPrevArrow);
    const modalNextArrowClassNames = cn('splide__arrow splide__arrow--next', styles.modalNextArrow);

    const sliderContainerRef = useRef<HTMLDivElement>(null);
    const viewportWidth = useViewportWidth();

    useEffect(() => {
        setSplideKey((prevKey) => prevKey + 1);
    }, [viewportWidth]);

    useEffect(() => {
        if (sliderContainerRef?.current) {
            const trackElement = sliderContainerRef.current.querySelector(`.${styles.sliderTrack}`) as HTMLDivElement;

            if (trackElement) {
                const isScrollable = trackElement.scrollWidth > trackElement.clientWidth;
                const shouldShowArrows =
                    isScrollable && (
                        (viewportWidth > responsiveBreakpointsPx.md) ||
                        (videoSlides.length > 4 && viewportWidth > responsiveBreakpointsPx.lg)
                    );
                setShowArrows(shouldShowArrows);
            }
        }
    }, [viewportWidth, videoSlides.length, sliderContainerRef]);

    const VideoGroupSliderOptions: Options = {
        rewind: false,
        focus: 0,
        omitEnd: true,
        autoWidth: true,
        arrows: showArrows,
        perMove: 1,
        gap: '16px',
        pagination: false,
        drag: true,
        speed: SLIDE_TRANSITION_SPEED_MS,
        easing: 'ease-out',
        breakpoints: {
            [responsiveBreakpointsPx.md]: {
                arrows: false,
                autoWidth: true,
            },
            [responsiveBreakpointsPx.sm]: {
                arrows: false,
                autoWidth: true,
            },
        },
        lazyLoad: true,
    };

    useEffect(() => {
        if (isModalOpen && splideRef.current) {
            setModalSlideSpeed(0);

            const timeoutId = setTimeout(() => {
                splideRef.current?.go(activeIndex);
                setModalSlideSpeed(SLIDE_TRANSITION_SPEED_MS);
            }, 0);

            return () => clearTimeout(timeoutId);
        }

        setModalSlideSpeed(0);
    }, [isModalOpen, activeIndex]);

    useEffect(() => {
        if (generalSplideRef.current) {
            generalSplideRef.current.go(generalActiveIndex);
        }
    }, [generalActiveIndex]);

    useClickOutside(ref, {}, (state, event) => {
        if (event) {
            if (event.target) {
                const target = event.target as HTMLElement;
                const closest = target.closest(MODAL_CLICK_THROUGH_EXCEPTIONS_SELECTOR);

                setFocusPause(!!closest);
            }
        }
    });

    const handleButtonClick = (index: number) => {
        setActiveIndex(index);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        const nextSyncTime =
            activeIndex >= 0
                ? (currentVideoTimesRef.current[activeIndex] ?? syncTimesRef.current[activeIndex] ?? 0)
                : 0;
        if (activeIndex >= 0) {
            syncTimesRef.current[activeIndex] = nextSyncTime;
        }
        setIsModalOpen(false);
        setActiveIndex(-1);
    };

    const handleVideoTimeSync = (index: number, time: number) => {
        currentVideoTimesRef.current[index] = time;
    };
    const handleFocus = (index: number) => {
        const splide = generalSplideRef?.current;
        if (splide) {
            setTimeout(() => {
                splide?.go(index);
            }, 150);
        }
    };

    return (
        <>
            {inlineVideo ? (
                <div className={styles.videoContainer}>{inlineVideo}</div>
            ) : (
                <div>
                    {isModalOpen && (
                        <FocusTrap
                            active={isModalOpen}
                            paused={focusPause}
                            focusTrapOptions={{
                                allowOutsideClick: true,
                            }}
                        >
                            <Modal
                                variant="video"
                                maxWidth={viewportWidth < 1188 ? viewportWidth : 1188}
                                backgroundTransparent={true}
                                open={isModalOpen}
                                onClose={handleCloseModal}
                                dataLocalpagename="overlay:videoslide"
                            >
                                <Splide
                                    className={styles.modalSlider}
                                    ref={splideRef}
                                    key={splideKey}
                                    hasTrack={false}
                                    options={{
                                        start: activeIndex,
                                        rewind: false,
                                        focus: 'center',
                                        arrows: videoSlides?.length > 1,
                                        perPage: 1,
                                        perMove: 1,
                                        pagination: false,
                                        drag: false,
                                        speed: modalSlideSpeed,
                                        easing: 'ease-out',
                                        lazyLoad: true,
                                    }}
                                    onMove={(splide, newIndex) => setActiveIndex(newIndex)}
                                >
                                    <SplideTrack>
                                        {videoSlides.map((slide, i) => (
                                            <SplideSlide key={i} className={styles.splideSlideItem}>
                                                {i === activeIndex ? (
                                                    <VideoPlayer
                                                        className={styles.videoModal}
                                                        {...slide}
                                                        initiatePlayer={true}
                                                        activeVideo={true}
                                                        resumeFromLastTime
                                                        isModalVideo={true}
                                                        onTimeSync={(time) => handleVideoTimeSync(i, time)}
                                                        syncTime={syncTimesRef.current[i]}
                                                        currentVideoTime={currentVideoTimesRef.current[i]}
                                                    />
                                                ) : (
                                                    <div className={styles.videoModal} />
                                                )}
                                            </SplideSlide>
                                        ))}
                                    </SplideTrack>
                                    <div className={`splide__arrows`}>
                                        <Button
                                            variant="primary"
                                            iconButton={true}
                                            size={'large'}
                                            type={'button'}
                                            colorSet={'overlay'}
                                            className={modalPrevArrowClassNames}
                                        >
                                            <ChevronIcon />
                                        </Button>

                                        <Button
                                            variant="primary"
                                            iconButton={true}
                                            size={'large'}
                                            type={'button'}
                                            colorSet={'overlay'}
                                            className={modalNextArrowClassNames}
                                        >
                                            <ChevronIcon />
                                        </Button>
                                    </div>
                                </Splide>
                            </Modal>
                        </FocusTrap>
                    )}
                    <div ref={sliderContainerRef}>
                        <Splide
                            className={sliderClassNames}
                            hasTrack={false}
                            options={VideoGroupSliderOptions}
                            onMove={(splide, newIndex) => {
                                setGeneralActiveIndex(newIndex);
                            }}
                            aria-label={sliderAriaLabel}
                            ref={generalSplideRef}
                            aria-live="off"
                        >
                            <SplideTrack className={styles.sliderTrack} aria-live="off">
                                {videoSlides?.map((slide, i) => {
                                    return (
                                        <SplideSlide key={i} aria-live="off">
                                            <Video
                                                {...slide}
                                                isVideoGroup
                                                handlePlayClick={() => handleButtonClick(i)}
                                                onFocus={() => handleFocus(i)}
                                            />
                                        </SplideSlide>
                                    );
                                })}
                            </SplideTrack>
                            <div className={`splide__arrows ${styles.carouselArrows}`}>
                                <button className={prevArrowClassNames} disabled={generalActiveIndex <= 0}>
                                    <ChevronIcon />
                                </button>
                                <button
                                    className={nextArrowClassNames}
                                    disabled={generalActiveIndex >= videoSlides.length - 1}
                                >
                                    <ChevronIcon />
                                </button>
                            </div>
                        </Splide>
                    </div>
                </div>
            )}
        </>
    );
}
